import { getFeedback } from "@sentry/browser";
import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";

type FeedbackDialog = {
  appendToDom: () => void;
  removeFromDom: () => void;
  open: () => void;
  close: () => void;
};

type UseSendFeedbackOptions = {
  // フィードバックイベントにマージするタグ。エラー報告時に発生元のSentryイベントIDを紐づけるなどに使う
  tags?: Record<string, string>;
};

type UseSendFeedbackResult = {
  sendFeedback: () => Promise<void>;
  isAvailable: boolean;
  isFeedbackOpen: boolean;
};

export const useSendFeedback = (options?: UseSendFeedbackOptions): UseSendFeedbackResult => {
  const isAvailable = !!getFeedback();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const tags = options?.tags;

  const sendFeedback = useCallback(async () => {
    const integration = getFeedback();
    if (!integration) return;

    try {
      // クロージャからformを参照するので、use-before-defineを避けるため先にletで宣言する
      let form: FeedbackDialog | null = null;
      const onFormFinished = () => {
        setIsFeedbackOpen(false);
        // createFormは呼び出しのたびにDOMノードを生成するため、終了時に除去して蓄積を防ぐ。
        form?.removeFromDom();
        // widgetは送信時にgetCurrentScope().setTags()でtagsを現在のスコープへ書き込むため、
        // このフィードバック固有のtagsを解除し、後続イベントや通常フィードバックへの漏れ込みを防ぐ。
        // （Sentry.setTagはisolationScopeに書くので、widgetと同じgetCurrentScopeを使う点に注意）
        if (tags) {
          const scope = Sentry.getCurrentScope();
          for (const key of Object.keys(tags)) {
            scope.setTag(key, undefined);
          }
        }
      };
      form = await integration.createForm({
        onFormOpen: () => setIsFeedbackOpen(true),
        // onFormCloseは「送信せず閉じた」時のみ、onFormSubmittedは「送信成功」時のみ発火するため、
        // tags書き込みとDOMノード残留が起きる送信成功パスを取りこぼさないよう両方で後処理する
        onFormClose: onFormFinished,
        onFormSubmitted: onFormFinished,
        tags,
      });
      form.appendToDom();
      form.open();
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [tags]);

  return { sendFeedback, isAvailable, isFeedbackOpen };
};
